import { type UseQueryResult, useQuery } from '@tanstack/react-query';

import api from 'src/axios';
import { getSessionForecastDataQueryKey } from 'src/keys';
import forecastService from 'src/services/forecast/forecastService';
import type { ForecastDataPoint } from 'src/types/domain/ForecastDataPoint';

export type OpenSessionForecastResponse = {
  data: ForecastDataPoint[];
  sku: string | null;
  validated_at: string | null;
  validated_number: number;
  total_products: number;
  current_page: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
  session_id: string;
  session_name?: string;
  page: number;
};

async function getSessionForecastData(
  sessionId: string,
  page: number,
): Promise<OpenSessionForecastResponse> {
  const { data } = await api.get<OpenSessionForecastResponse>(
    `/sales-forecasting-sessions/${sessionId}/forecast?page=${page}`,
  );

  return {
    ...data,
    data: data.sku
      ? forecastService.formatForecastData(data.data, data.sku, sessionId)
      : data.data,
    page,
  };
}

export const useSessionForecastDataQuery = <T = OpenSessionForecastResponse>(
  sessionId: string,
  page = 1,
): UseQueryResult<T, Error> => {
  return useQuery({
    queryKey: getSessionForecastDataQueryKey(sessionId, page),
    queryFn: async () => (await getSessionForecastData(sessionId, page)) as T,
    enabled: Boolean(sessionId),
  });
};
